import * as Sentry from '@sentry/react';
import {
  Account_Invite,
  AccountInviteCompanyType,
  AccountType,
  Address_Create,
  CompanyType,
  Contact_Create,
  getV1CompaniesId,
  ModelError_Response,
  postV1CompaniesCompanyIdAccountsInvite,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';

import { Account } from '~hooks/useAccount/models';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import { Company } from '~hooks/useCompany/models';
import { InviteType } from '~types/InviteTypes';

import { ConnectedAccountContactPanelHandler } from '../ConnectedAccountContactPanel/ConnectedAccountContactPanel';
import { NotesPanelHandler } from '../NotesPanel/NotesPanel';
import { AccountConfigurationPanelHandler } from './AccountConfigurationPanel/AccountConfigurationPanel';
import { AccountDetailsPanelHandler } from './AccountDetailsPanel/AccountDetailsPanel';

// Maps the (non-super) CompanyType the form collects to the AccountInviteCompanyType
// enum member Account_Invite.company_type expects. Both enums share the same
// underlying string values but are distinct nominal types.
const ACCOUNT_INVITE_COMPANY_TYPE_BY_COMPANY_TYPE: Record<
  Exclude<CompanyType, CompanyType.SUPER>,
  AccountInviteCompanyType
> = {
  [CompanyType.BROKER]: AccountInviteCompanyType.BROKER,
  [CompanyType.CONTRACTOR]: AccountInviteCompanyType.CONTRACTOR,
  [CompanyType.HAULER]: AccountInviteCompanyType.HAULER,
  [CompanyType.OWNER_OPERATOR]: AccountInviteCompanyType.OWNER_OPERATOR,
  [CompanyType.PRODUCER]: AccountInviteCompanyType.PRODUCER,
};

// This is to avoid fetching the same company address multiple times
const alreadyFetchedCompanyAddressByCompanyId = new Map<string, Address_Create>();

export async function collectDetailsForNewAccount(
  ref: React.RefObject<AccountDetailsPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  let address = AddressItem.deparseCreate(data.address as AddressItem);
  const hasEmptyAddress = !address?.thoroughfare;

  if (hasEmptyAddress) {
    const alreadyFetchedCompanyAddress = alreadyFetchedCompanyAddressByCompanyId.get(
      data.company.value,
    );

    if (alreadyFetchedCompanyAddress) {
      address = alreadyFetchedCompanyAddress;
    } else {
      const response = await getV1CompaniesId({ path: { id: data.company.value } });
      const parsed = Company.parse(response.data.data);

      address = AddressItem.deparseCreate(parsed.address);

      if (address) {
        alreadyFetchedCompanyAddressByCompanyId.set(data.company.value, address);
      }
    }
  }

  if (!address) {
    const error = new Error('[collectDetailsForNewAccount] missing address');
    Sentry.captureException(error);
    throw error;
  }

  const values: Pick<
    Account_Invite,
    'name' | 'address' | 'department_id' | 'external_id' | 'label_ids'
  > = {
    name: data.name,
    address,
    department_id: data.department?.value || null,
    external_id: data.externalId || null,
    label_ids: data.labels?.map((label) => label.id),
  };

  return { values, companyId: data.company.value };
}

export async function collectContactsForNewAccount(
  ref: React.RefObject<ConnectedAccountContactPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const contact: Contact_Create = {
    name: `${data.primaryContact.firstName} ${data.primaryContact.lastName}`,
    email: data.primaryContact.email || undefined,
    phone: data.primaryContact.phone || undefined,
  };
  const isEmailInviteSelected = data.inviteTypes?.find(
    (option) => option.value === InviteType.EMAIL,
  );
  const isSmsInviteSelected = data.inviteTypes?.find(
    (option) => option.value === InviteType.SMS,
  );

  const values: Pick<Account_Invite, 'contact' | 'send_invite'> = {
    contact,
    send_invite: {
      email: Boolean(isEmailInviteSelected),
      sms: Boolean(isSmsInviteSelected),
    },
  };

  return values;
}

export async function collectConfigurationForNewAccount(
  ref: React.RefObject<AccountConfigurationPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const companyType = data.companyType as {
    value: Exclude<CompanyType, CompanyType.SUPER>;
  } | null;

  const values: Pick<
    Account_Invite,
    | 'account_types'
    | 'company_type'
    | 'auto_accept_orders'
    | 'jobs_splittable'
    | 'equipment_options_restricted'
  > = {
    account_types: data.accountTypes as AccountType[],
    company_type: companyType?.value
      ? ACCOUNT_INVITE_COMPANY_TYPE_BY_COMPANY_TYPE[companyType.value]
      : undefined,
    auto_accept_orders: false,
    jobs_splittable: data.jobsSplittable ?? false,
    equipment_options_restricted: data.equipmentOptionsRestricted ?? false,
  };

  return values;
}

export async function collectNotesForNewAccount(ref: React.RefObject<NotesPanelHandler>) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const values: Pick<Account_Invite, 'notes'> = {
    notes: data.notes?.trim() || null,
  };

  return values;
}

export async function createAccount(data: Account_Invite, targetCompanyId: string) {
  try {
    const response = await postV1CompaniesCompanyIdAccountsInvite({
      path: { 'company-id': targetCompanyId },
      body: data,
    });

    return { account: Account.parse(response.data.data) };
  } catch (error) {
    const isUnprocessableEntityError = isAxiosError(error) && error.status === 422;

    if (isUnprocessableEntityError) {
      const typedError = error as AxiosError<ModelError_Response>;

      return { errors: typedError.response?.data.error.errors ?? [] };
    }

    throw error;
  }
}
